import axiosClient from "../api/axiosClient";

// --- Album media in-memory cache ---
// Persists across route navigation within the same browser session.
// Key: `${chatRoomId}-${albumId}-${orderBy}`
const _albumMediaCache = new Map(); // { data, timestamp }
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const invalidateAlbumCache = (chatRoomId, albumId) => {
  const prefix = `${chatRoomId}-${albumId}`;
  for (const key of _albumMediaCache.keys()) {
    if (key.startsWith(prefix)) _albumMediaCache.delete(key);
  }
};
// ------------------------------------

export const getGalleriesByEvent = (eventId) =>
  axiosClient.get(`/Chat/GetGalleriesByEvent/${eventId}`);

export const getShareGalleryRanking = (eventId) =>
  axiosClient.get(`/Events/share-gallery-ranking/${eventId}`);

export const createGallery = (data) =>
  axiosClient.post("/Events/EventGallery/Create", data);

export const createAlbum = (data) =>
  axiosClient.post("/Events/GalleryAlbum/Create", data);

export const updateEventGallery = (galleryId, data) =>
  axiosClient.put(`/Events/UpdateEventGallery/${galleryId}`, data);

export const updateGalleryAlbum = (data) =>
  axiosClient.put("/Events/GalleryAlbum/Update", data);

export const getARoomAlbumViewerRequests = (albumId) =>
  axiosClient.get(`/Events/GalleryAlbum/${albumId}/viewer-requests`);

export const getARoomAlbumApprovedViewers = (albumId) =>
  axiosClient.get(`/Events/GalleryAlbum/${albumId}/approved-viewers`);

export const approveARoomAlbumViewer = (albumId, userId) =>
  axiosClient.post(`/Events/GalleryAlbum/${albumId}/viewer-approvals`, { userId });

export const revokeARoomAlbumViewer = (albumId, userId) =>
  axiosClient.delete(`/Events/GalleryAlbum/${albumId}/viewer-approvals/${userId}`);

export const archiveEventGallery = ({ galleryId, eventId, name }) =>
  updateEventGallery(galleryId, {
    EventId: Number(eventId),
    Name: name,
    IsArchieved: true,
  });

/** Activates/deactivates an @Room's fundraising support banner, ported from mobile's a-room-support-banner screen. */
export const setFundraisingBannerActive = (galleryId, eventId, isActive) =>
  updateEventGallery(galleryId, {
    EventGalleryId: Number(galleryId),
    EventId: Number(eventId),
    IsFundRaisingActive: isActive,
  });

/** Activates/deactivates a Share Gallery — the only lifecycle action it supports (no delete/rename). */
export const setShareGalleryActive = (galleryId, eventId, isActive) =>
  updateEventGallery(galleryId, {
    EventGalleryId: Number(galleryId),
    EventId: Number(eventId),
    ShareBannerActive: isActive,
  });

export const archiveGalleryAlbum = ({ albumId, name }) =>
  updateGalleryAlbum({
    AlbumId: Number(albumId),
    Name: name,
    IsArchived: true,
  });

export const getAlbumMedia = (chatRoomId, albumId, orderBy = "desc") => {
  const cacheKey = `${chatRoomId}-${albumId}-${orderBy}`;
  const cached = _albumMediaCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return Promise.resolve({ data: cached.data });
  }
  return axiosClient
    .get(`/Chat/${chatRoomId}/Gallery/Media`, {
      params: { AlbumId: albumId, OrderBy: orderBy },
    })
    .then((res) => {
      _albumMediaCache.set(cacheKey, { data: res.data, timestamp: Date.now() });
      return res;
    });
};

export const uploadMedia = (formData) =>
  axiosClient.post("/Gallery/Upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const archiveMedia = (ids) =>
  axiosClient.put(
    "/Chat/Gallery/Parminent_Upload",
    ids.map((id) => ({ id, IsArchived: true }))
  );

export const moderateSubPromoterMedia = (mediaId, action, reason) =>
  axiosClient.post("/Chat/Gallery/Moderate", { mediaId: Number(mediaId), action, reason });

export const setAlbumThumbnail = (mediaId, isAlbumThumbnail, displayOrder) =>
  axiosClient.put("/Chat/Gallery/Parminent_Upload", [
    { id: mediaId, IsAlbumThumbnail: isAlbumThumbnail, ...(displayOrder != null ? { displayOrder } : {}) },
  ]);

export const setHomeImage = (mediaId, isHomeImage, displayOrder) =>
  axiosClient.put("/Chat/Gallery/Parminent_Upload", [
    { id: mediaId, IsHomeImage: isHomeImage, ...(displayOrder != null ? { displayOrder } : {}) },
  ]);

export const updateGalleryOrder = (orderData) =>
  axiosClient.post("/Chat/UpdateGalleryOrder", orderData);

export const updateAlbumOrder = (orderData) =>
  axiosClient.post("/Chat/UpdateAlbumOrder", orderData);
